import { Routes, Route, Navigate } from 'react-router-dom'
import NotFoundPage from '../pages/NotFoundPage'
import { useAuthStore } from '../stores/auth'
import LoginPage from '../pages/auth/LoginPage'
import OwnerLayout from '../components/layout/OwnerLayout'
import ManagerLayout from '../components/layout/ManagerLayout'
import MechanicLayout from '../components/layout/MechanicLayout'
import DashboardPage from '../pages/owner/DashboardPage'
import PointsPage from '../pages/owner/PointsPage'
import BikesPage from '../pages/owner/BikesPage'
import TariffsPage from '../pages/owner/TariffsPage'
import ManagersPage from '../pages/owner/ManagersPage'
import RentalsPage from '../pages/owner/RentalsPage'
import ManualRentalPage from '../pages/owner/ManualRentalPage'
import SettingsPage from '../pages/owner/SettingsPage'
import AuditPage from '../pages/owner/AuditPage'
import ShiftsPage from '../pages/owner/ShiftsPage'
import JournalPage from '../pages/owner/JournalPage'
import PaymentsPage from '../pages/owner/PaymentsPage'
import ReportsPage from '../pages/owner/ReportsPage'
import ShiftPage from '../pages/manager/ShiftPage'
import ScanPage from '../pages/manager/ScanPage'
import RentalFormPage from '../pages/manager/RentalFormPage'
import ActiveRentalsPage from '../pages/manager/ActiveRentalsPage'
import ProfilePage from '../pages/manager/ProfilePage'
import ManagerShiftsPage from '../pages/manager/ManagerShiftsPage'
import MechanicBikesPage from '../pages/mechanic/MechanicBikesPage'
import RegisterPage from '../pages/auth/RegisterPage'
import BikePublicPage from '../pages/public/BikePublicPage'
import AdminPage from '../pages/admin/AdminPage'
import AdminAccessPage from '../pages/admin/AdminAccessPage'
import OnboardingPage from '../pages/owner/OnboardingPage'
import AttentionPage from '../pages/owner/AttentionPage'
import WarehousePage from '../pages/owner/WarehousePage'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function homeForRole(role: string): string {
  if (role === 'owner') return '/owner'
  if (role === 'mechanic') return '/mechanic'
  return '/manager'
}

function RequireOwner({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'owner') return <Navigate to={homeForRole(user.role)} replace />
  return <>{children}</>
}

function RequireMechanic({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'mechanic') return <Navigate to={homeForRole(user.role)} replace />
  return <>{children}</>
}

function RequireManager({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'manager') return <Navigate to={homeForRole(user.role)} replace />
  return <>{children}</>
}

export function AppRouter() {
  const user = useAuthStore((s) => s.user)

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homeForRole(user.role)} replace /> : <LoginPage />} />

      {/* Owner routes */}
      <Route path="/owner" element={<RequireOwner><OwnerLayout /></RequireOwner>}>
        <Route index element={<DashboardPage />} />
        <Route path="attention" element={<AttentionPage />} />
        <Route path="points" element={<PointsPage />} />
        <Route path="bikes" element={<BikesPage />} />
        <Route path="tariffs" element={<TariffsPage />} />
        <Route path="managers" element={<ManagersPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="rentals" element={<RentalsPage />} />
        <Route path="rentals/manual" element={<ManualRentalPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="journal" element={<JournalPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="warehouse" element={<WarehousePage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
      </Route>

      {/* Manager routes */}
      <Route path="/manager" element={<RequireManager><ManagerLayout /></RequireManager>}>
        <Route index element={<ShiftPage />} />
        <Route path="scan" element={<ScanPage />} />
        <Route path="rental/new" element={<RentalFormPage />} />
        <Route path="rentals" element={<ActiveRentalsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="shifts" element={<ManagerShiftsPage />} />
      </Route>

      {/* Mechanic routes */}
      <Route path="/mechanic" element={<RequireMechanic><MechanicLayout /></RequireMechanic>}>
        <Route index element={<MechanicBikesPage />} />
        <Route path="warehouse" element={<WarehousePage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Public QR page — accessible without auth */}
      <Route path="/bike" element={<BikePublicPage />} />

      {/* Admin panel — protected by ADMIN_SECRET, no tenant auth required */}
      <Route path="/admin" element={<AdminPage />} />
      {/* Admin temp-access bridge — logs in as tenant owner without password change */}
      <Route path="/admin-access" element={<AdminAccessPage />} />

      <Route path="/register" element={user ? <Navigate to={homeForRole(user.role)} replace /> : <RegisterPage />} />
      <Route path="/" element={<Navigate to={user ? homeForRole(user.role) : '/login'} replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
